/**
 * Field dictionary for the history drawer.
 *
 * Maps a stored field path to what the reader should see: a translation key, the
 * section it belongs to, and the kind of value it holds. The audit trail records
 * whole subdocuments, and the read service expands them into dot paths, so the
 * keys here are the expanded leaf paths ("holidayLeaveCalculation.numberOfHolidayDaysUsed"),
 * not the top-level ones.
 *
 * An unknown path is never dropped — `describeField` falls back to the raw path,
 * so a field added to a model tomorrow still shows up, just untranslated.
 *
 * The `type` is what makes a value readable, and it is deliberately the ONLY
 * thing that turns a number into a date. A magnitude heuristic ("13 digits means
 * epoch") was measured against the real data and would have rendered JMBG
 * (2810987762623), bank account parts (0030042674020) and PIB as dates. Only a
 * field the dictionary declares a date is ever parsed as one.
 */

export const HISTORY_SECTION = Object.freeze({
  holiday: 'holiday',
  employment: 'employment',
  personal: 'personal',
  contact: 'contact',
  bank: 'bank',
  earnings: 'earnings',
  settings: 'settings',
  documents: 'documents',
  access: 'access',
  system: 'system',
  other: 'other',
});

/**
 * Order sections appear in. Anything not listed sorts last.
 */
export const SECTION_ORDER = Object.freeze([
  HISTORY_SECTION.holiday,
  HISTORY_SECTION.employment,
  HISTORY_SECTION.personal,
  HISTORY_SECTION.contact,
  HISTORY_SECTION.bank,
  HISTORY_SECTION.earnings,
  HISTORY_SECTION.settings,
  HISTORY_SECTION.documents,
  HISTORY_SECTION.access,
  HISTORY_SECTION.system,
  HISTORY_SECTION.other,
]);

export const FIELD_TYPE = Object.freeze({
  text: 'text',
  number: 'number',
  days: 'days',
  hours: 'hours',
  money: 'money',
  percent: 'percent',
  date: 'date',
  datetime: 'datetime',
  bool: 'bool',
  enum: 'enum',
  ref: 'ref',
  array: 'array',
  sensitive: 'sensitive',
  color: 'color',
});

const S = HISTORY_SECTION;
const T = FIELD_TYPE;

/**
 * Compact descriptor builder.
 *
 * @param {string} i18nKey - Key under the `history` namespace, without the `field.` prefix.
 * @param {string} section - One of HISTORY_SECTION.
 * @param {string} [type] - One of FIELD_TYPE.
 * @param {string} [enumSet] - Enum family, for `enum.<set>.<value>` lookups.
 */
const f = (i18nKey, section, type = T.text, enumSet) => ({
  i18nKey,
  section,
  type,
  enumSet,
});

/* ------------------------------------------------------------------ *
 * Paths that mean the same thing on every model.
 * ------------------------------------------------------------------ */
const COMMON = {
  _id: f('id', S.system),
  id: f('id', S.system),
  __v: f('version', S.system, T.number),
  status: f('status', S.system, T.enum, 'status'),
  createdAt: f('createdAt', S.system, T.datetime),
  updatedAt: f('updatedAt', S.system, T.datetime),
  createdBy: f('createdBy', S.system, T.ref),
  modifiedBy: f('modifiedBy', S.system, T.ref),
  company: f('company', S.employment, T.ref),
  market: f('market', S.settings),
  firstName: f('firstName', S.personal),
  lastName: f('lastName', S.personal),
  email: f('email', S.contact),
  address: f('address', S.contact),
  city: f('city', S.contact),
  country: f('country', S.contact),
  zip: f('zip', S.contact),
  phone: f('phone', S.contact),
  title: f('title', S.other),
  name: f('name', S.other),
  deleted: f('deleted', S.system, T.bool),
};

/* ------------------------------------------------------------------ *
 * Employee
 * ------------------------------------------------------------------ */
const EMPLOYEE = {
  firstName: f('firstName', S.personal),
  lastName: f('lastName', S.personal),
  status: f('status', S.employment, T.enum, 'employeeStatus'),
  userId: f('linkedUser', S.access, T.ref),
  employeeSector: f('sector', S.employment, T.ref),
  supersededBy: f('supersededBy', S.system, T.ref),
  archiveReason: f('archiveReason', S.system),
  archivedAt: f('archivedAt', S.system, T.datetime),
  archivedEmail: f('archivedEmail', S.system),

  // Personal data
  'generalData.personalNumber': f('personalNumber', S.personal),
  'generalData.isResident': f('isResident', S.personal, T.bool),
  'generalData.otherRemarks': f('otherRemarks', S.personal),
  'additionalPersonalInfo.birthDate': f('birthDate', S.personal, T.date),
  'additionalPersonalInfo.gender': f('gender', S.personal, T.enum, 'gender'),
  'additionalPersonalInfo.educationLevel': f(
    'educationLevel',
    S.personal,
    T.enum,
    'educationLevel'
  ),
  'additionalPersonalInfo.educationLevelOther': f(
    'educationLevelOther',
    S.personal
  ),
  'additionalPersonalInfo.customId': f('customId', S.personal),
  'additionalPersonalInfo.familyCelebrationDate': f(
    'familyCelebrationDate',
    S.personal,
    T.date
  ),
  'additionalPersonalInfo.children': f('children', S.personal, T.array),
  'additionalPersonalInfo.children[].firstName': f('childFirstName', S.personal),
  'additionalPersonalInfo.children[].lastName': f('childLastName', S.personal),
  'additionalPersonalInfo.children[].birthDate': f(
    'childBirthDate',
    S.personal,
    T.date
  ),
  'additionalPersonalInfo.workExperience.years': f(
    'experienceYears',
    S.personal,
    T.number
  ),
  'additionalPersonalInfo.workExperience.months': f(
    'experienceMonths',
    S.personal,
    T.number
  ),
  'additionalPersonalInfo.workExperience.days': f(
    'experienceDays',
    S.personal,
    T.number
  ),
  'additionalPersonalInfo.previousEmployments': f(
    'previousEmployments',
    S.personal,
    T.array
  ),
  previousEmployments: f('previousEmployments', S.personal, T.array),

  // Contact
  'generalData.email': f('email', S.contact),
  'generalData.phone': f('phone', S.contact),
  'generalData.address': f('address', S.contact),
  'generalData.city': f('city', S.contact),
  'generalData.country': f('country', S.contact),
  'generalData.municipality': f('municipality', S.contact),
  'generalData.zip': f('zip', S.contact),

  // Bank
  'generalData.bankName': f('bankName', S.bank),
  'generalData.bankAccounts': f('bankAccount', S.bank),
  'generalData.bankAccounts.part1': f('bankAccountPart1', S.bank),
  'generalData.bankAccounts.part2': f('bankAccountPart2', S.bank),
  'generalData.bankAccounts.part3': f('bankAccountPart3', S.bank),

  // Employment
  'generalData.position': f('position', S.employment),
  'generalData.positionEng': f('positionEng', S.employment),
  'generalData.jobPosition': f('jobPosition', S.employment, T.ref),
  'generalData.orgUnit': f('orgUnit', S.employment),
  'employmentSettings.startDate': f('startDate', S.employment, T.date),
  'employmentSettings.endDate': f('endDate', S.employment, T.date),
  'employmentSettings.employmentType': f(
    'employmentType',
    S.employment,
    T.enum,
    'employmentType'
  ),
  'employmentSettings.workTimeType': f(
    'workTimeType',
    S.employment,
    T.enum,
    'workTimeType'
  ),
  'employmentSettings.workTimeCustomType': f(
    'workTimeCustomType',
    S.employment,
    T.percent
  ),
  'employmentSettings.shiftWorker': f('shiftWorker', S.employment, T.bool),
  'employmentSettings.isHrOfficer': f('isHrOfficer', S.access, T.bool),
  'employmentSettings.dateOfWorkPermissionForeigner': f(
    'workPermitDate',
    S.employment,
    T.date
  ),

  // Career history
  careerHistory: f('careerHistory', S.employment, T.array),
  'careerHistory[].changeType': f('careerChangeType', S.employment),
  'careerHistory[].dateOfRequest': f('careerDate', S.employment, T.date),
  'careerHistory[].oldPosition': f('careerOldPosition', S.employment),
  'careerHistory[].newPosition': f('careerNewPosition', S.employment),
  'careerHistory[].oldPositionEng': f('careerOldPositionEng', S.employment),
  'careerHistory[].newPositionEng': f('careerNewPositionEng', S.employment),
  'careerHistory[].oldSector': f('careerOldSector', S.employment, T.ref),
  'careerHistory[].newSector': f('careerNewSector', S.employment, T.ref),
  'careerHistory[].companyFrom': f('careerCompanyFrom', S.employment, T.ref),
  'careerHistory[].companyTo': f('careerCompanyTo', S.employment, T.ref),
  'careerHistory[].gapDays': f('careerGapDays', S.employment, T.days),
  'careerHistory[].notes': f('careerNotes', S.employment),
  'careerHistory[].previousEmployeeId': f(
    'careerPreviousEmployee',
    S.employment,
    T.ref
  ),
  'careerHistory[].previousEmploymentEndDate': f(
    'careerPreviousEnd',
    S.employment,
    T.date
  ),

  // Annual leave
  'holidayLeaveCalculation.numberOfHolidayDays': f(
    'holidayDaysTotal',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.numberOfHolidayDaysUsed': f(
    'holidayDaysUsed',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.numberOfHolidayDaysCurrent': f(
    'holidayDaysCurrent',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.numberOfHolidayDaysPast': f(
    'holidayDaysPast',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.numberOfHolidayDaysContract': f(
    'holidayDaysContract',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.numberOfHolidayDaysByEndOfContract': f(
    'holidayDaysByEndOfContract',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.numberOfAdditionalHolidayDays': f(
    'holidayDaysAdditional',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.holidayDaysYear': f(
    'holidayDaysYear',
    S.holiday,
    T.number
  ),
  'holidayLeaveCalculation.employeeTrackerType': f(
    'employeeTrackerType',
    S.holiday,
    T.enum,
    'employeeTrackerType'
  ),
  'holidayLeaveCalculation.oldHolidayExpiredDays': f(
    'oldHolidayExpiredDays',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.oldHolidayExpiredAt': f(
    'oldHolidayExpiredAt',
    S.holiday,
    T.date
  ),
  'holidayLeaveCalculation.oldHolidayExpiredForYear': f(
    'oldHolidayExpiredForYear',
    S.holiday,
    T.number
  ),
  holidayYearChangeDoneFor: f(
    'holidayYearChangeDoneFor',
    S.holiday,
    T.number
  ),

  // The calculation carries more than the schema declares: the employment
  // window and the working figures behind a proration are written alongside the
  // stored day counts. They were read off the audit trail itself rather than the
  // model, because that is where the reader meets them — without an entry each
  // one shows as a raw dotted path.
  'holidayLeaveCalculation.startDate': f('holidayPeriodStart', S.holiday, T.date),
  'holidayLeaveCalculation.endDate': f('holidayPeriodEnd', S.holiday, T.date),
  'holidayLeaveCalculation.edgeDate': f('holidayEdgeDate', S.holiday, T.date),
  'holidayLeaveCalculation.employmentType': f(
    'holidayEmploymentType',
    S.holiday,
    T.enum,
    'employmentType'
  ),
  'holidayLeaveCalculation.currentlyAvailableHolidayDays': f(
    'holidayDaysAvailableNow',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.currentDaysYear': f(
    'holidayCurrentDaysYear',
    S.holiday,
    T.number
  ),
  'holidayLeaveCalculation.pastDaysYear': f(
    'holidayPastDaysYear',
    S.holiday,
    T.number
  ),
  'holidayLeaveCalculation.usedCurrentDays': f(
    'holidayUsedCurrentDays',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.usedPastDays': f(
    'holidayUsedPastDays',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.numberOfNewDaysConsumed': f(
    'holidayNewDaysConsumed',
    S.holiday,
    T.days
  ),
  'holidayLeaveCalculation.holidayLeaveForYear': f(
    'holidayLeaveForYear',
    S.holiday,
    T.number
  ),
  'holidayLeaveCalculation.isBothYearsHoliday': f(
    'holidayBothYears',
    S.holiday,
    T.bool
  ),
  additionalHolidayDays: f('additionalHolidayDays', S.holiday, T.array),
  'additionalHolidayDays[].days': f('additionalHolidayDaysCount', S.holiday, T.days),
  'additionalHolidayDays[].reason': f('additionalHolidayDaysReason', S.holiday),
  'additionalHolidayDays[].year': f('additionalHolidayDaysYear', S.holiday, T.number),

  // The same annual-leave counters also arrive as top-level audit keys, from the
  // leave pipelines that write them directly rather than through the subdocument.
  numberOfHolidayDays: f('holidayDaysTotal', S.holiday, T.days),
  numberOfHolidayDaysUsed: f('holidayDaysUsed', S.holiday, T.days),
  numberOfHolidayDaysCurrent: f('holidayDaysCurrent', S.holiday, T.days),
  numberOfHolidayDaysPast: f('holidayDaysPast', S.holiday, T.days),
  numberOfHolidayDaysContract: f('holidayDaysContract', S.holiday, T.days),
  numberOfHolidayDaysByEndOfContract: f(
    'holidayDaysByEndOfContract',
    S.holiday,
    T.days
  ),
  holidayDaysYear: f('holidayDaysYear', S.holiday, T.number),
  holidayLeaveForYear: f('holidayLeaveForYear', S.holiday, T.number),
  isBothYearsHoliday: f('isBothYearsHoliday', S.holiday, T.bool),

  // Special absence
  'specialAbsence.enabled': f('specialAbsenceEnabled', S.holiday, T.bool),
  'specialAbsence.allowedDays': f('specialAbsenceAllowedDays', S.holiday, T.days),
  'specialAbsence.limits.absence': f('specialAbsenceLimitAbsence', S.holiday, T.days),
  'specialAbsence.limits.sick': f('specialAbsenceLimitSick', S.holiday, T.days),
  'specialAbsence.limits.other': f('specialAbsenceLimitOther', S.holiday, T.days),
  'specialAbsence.entries': f('specialAbsenceEntries', S.holiday, T.array),

  // Earnings
  'allowances.mealAllowance': f('mealAllowance', S.earnings, T.money),
  'allowances.travelAllowance': f('travelAllowance', S.earnings, T.money),
  'allowances.holidayAllowance': f('holidayAllowance', S.earnings, T.money),
  'allowances.workFromHomeAllowance': f('workFromHomeAllowance', S.earnings, T.money),
  'allowances.defaultTimesheetComment': f('defaultTimesheetComment', S.earnings),
  'otherAllowances.bonus': f('bonus', S.earnings, T.money),
  'otherAllowances.bonusBasis': f(
    'bonusBasis',
    S.earnings,
    T.enum,
    'bonusBasis'
  ),
  'otherAllowances.otherIncome': f('otherIncome', S.earnings, T.money),
  'otherAllowances.otherExpenses': f('otherExpenses', S.earnings, T.money),
  'otherAllowances.suspension': f('suspension', S.earnings, T.money),
  'otherAllowances.nonMonetaryBenefit': f('nonMonetaryBenefit', S.earnings, T.money),
  'suspensionDrawer.suspensionAmount': f('suspensionAmount', S.earnings, T.money),
  'suspensionDrawer.currencySuspension': f('suspensionCurrency', S.earnings),
  'suspensionDrawer.dateOfCreation': f('suspensionDate', S.earnings, T.date),
  'suspensionDrawer.editedReasonOfSuspension': f('suspensionReason', S.earnings),
  'suspensionDrawer.editedReasonOfSuspensionEng': f('suspensionReasonEng', S.earnings),

  // The subdocuments themselves. Expansion only reaches inside when both sides
  // are objects, so a first write — `null → {…}` — arrives as one line named
  // after the container and has to be labelled like any other field.
  allowances: f('allowances', S.earnings),
  otherAllowances: f('otherAllowances', S.earnings),
  additionalPersonalInfo: f('additionalPersonalInfo', S.personal),
  'additionalPersonalInfo.placeOfBirth': f('placeOfBirth', S.personal),
  'otherAllowances.bonusVariations': f('bonusVariations', S.earnings),
  'otherAllowances.suspensionVariations': f('suspensionVariations', S.earnings),
  'otherAllowances.nonMonetaryBenefitVariations': f(
    'nonMonetaryBenefitVariations',
    S.earnings
  ),
  'otherAllowances.suspensionVariationsDates': f(
    'suspensionVariationsDates',
    S.earnings
  ),

  // Documents
  attachments: f('attachments', S.documents, T.array),
  'attachments[].originalName': f('attachmentName', S.documents),
  'attachments[].type': f('attachmentType', S.documents),
  'attachments[].location': f('attachmentLocation', S.documents),
  deletedFileIds: f('deletedFileIds', S.documents, T.array),
};

/* ------------------------------------------------------------------ *
 * Company
 * ------------------------------------------------------------------ */
const COMPANY = {
  // The account itself, alongside the `part1/2/3` entries below: the model
  // stores a string and its getter splits it, so the trail holds both shapes.
  'generalData.bankAccount': f('bankAccount', S.bank, T.text),
  name: f('companyName', S.settings),
  status: f('status', S.system, T.enum, 'status'),
  market: f('market', S.settings),
  sectors: f('sectors', S.settings, T.array),
  publicHolidays: f('publicHolidays', S.settings, T.array),

  'generalData.pib': f('pib', S.settings),
  'generalData.code': f('registrationNumber', S.settings),
  'generalData.address': f('address', S.contact),
  'generalData.city': f('city', S.contact),
  'generalData.zip': f('zip', S.contact),
  'generalData.email': f('email', S.contact),
  'generalData.phoneNumber': f('phone', S.contact),
  'generalData.emailReceivers': f('emailReceivers', S.contact, T.array),
  'generalData.apiKey': f('apiKey', S.access, T.sensitive),
  'generalData.bankAccount.part1': f('bankAccountPart1', S.bank),
  'generalData.bankAccount.part2': f('bankAccountPart2', S.bank),
  'generalData.bankAccount.part3': f('bankAccountPart3', S.bank),
  'generalData.bankAccountsArray': f('bankAccounts', S.bank, T.array),

  'allowances.mealAllowance': f('mealAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.travelAllowance': f('travelAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.holidayAllowance': f('holidayAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.workFromHomeAllowance': f('workFromHomeAllowance', S.earnings, T.enum, 'allowanceType'),
  // Rows written before the diff read the raw document carry the *transformed*
  // allowance — `{key, value, label}` rather than the method itself — so the
  // method reads off three sub-paths as well as off the field.
  'allowances.mealAllowance.key': f('mealAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.mealAllowance.value': f('mealAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.mealAllowance.label': f('mealAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.travelAllowance.key': f('travelAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.travelAllowance.value': f('travelAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.travelAllowance.label': f('travelAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.holidayAllowance.key': f('holidayAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.holidayAllowance.value': f('holidayAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.holidayAllowance.label': f('holidayAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.workFromHomeAllowance.key': f('workFromHomeAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.workFromHomeAllowance.value': f('workFromHomeAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.workFromHomeAllowance.label': f('workFromHomeAllowance', S.earnings, T.enum, 'allowanceType'),
  'allowances.isSpecialHolidayAllowanceCalculation': f(
    'specialHolidayAllowanceCalc',
    S.earnings,
    T.bool
  ),
  'currencies.mealAllowanceCurrency': f('mealAllowanceCurrency', S.earnings),
  'currencies.travelAllowanceCurrency': f('travelAllowanceCurrency', S.earnings),
  'currencies.holidayAllowanceCurrency': f('holidayAllowanceCurrency', S.earnings),
  'currencies.workFromHomeAllowanceCurrency': f('workFromHomeAllowanceCurrency', S.earnings),
  'currencies.otherIncomeCurrency': f('otherIncomeCurrency', S.earnings),
  'currencies.otherExpensesCurrency': f('otherExpensesCurrency', S.earnings),
  'currencies.bonusCurrency': f('bonusCurrency', S.earnings),
  'currencies.suspensionCurrency': f('suspensionCurrency', S.earnings),
  'currencies.nonMonetaryBenefitCurrency': f('nonMonetaryBenefitCurrency', S.earnings),

  'otherAllowances.bonus': f('bonus', S.earnings),
  'otherAllowances.otherIncome': f('otherIncome', S.earnings),
  'otherAllowances.otherExpenses': f('otherExpenses', S.earnings),
  'otherAllowances.suspension': f('suspension', S.earnings),
  'otherAllowances.nonMonetaryBenefit': f('nonMonetaryBenefit', S.earnings),

  'calculationSettings.bonus': f('calcBonus', S.earnings, T.bool),
  'calculationSettings.otherIncome': f('calcOtherIncome', S.earnings, T.bool),
  'calculationSettings.otherExpenses': f('calcOtherExpenses', S.earnings, T.bool),
  'calculationSettings.suspension': f('calcSuspension', S.earnings, T.bool),
  'calculationSettings.nonMonetaryBenefit': f('calcNonMonetaryBenefit', S.earnings, T.bool),
  'calculationSettings.overTime2': f('calcOvertime2', S.earnings, T.bool),
  'calculationSettings.overtimeManualAmount': f('calcOvertimeManual', S.earnings, T.bool),
  'calculationSettings.extendedAbExport': f('extendedAbExport', S.earnings, T.bool),
  'calculationSettings.extendedExportBindings': f('extendedExportBindings', S.earnings, T.array),
  'calculationSettings.workTypes': f('workTypes', S.earnings, T.array),
  // The element itself, for changes recorded against a whole entry rather
  // than one of its fields.
  'calculationSettings.workTypes[]': f('workTypes', S.earnings),
  'calculationSettings.workTypes[].label': f('workTypeLabel', S.earnings),
  'calculationSettings.workTypes[].key': f('workTypeKey', S.earnings),
  'calculationSettings.workTypes[].value': f('workTypeValue', S.earnings),
  'calculationSettings.workTypes[].code': f('workTypeCode', S.earnings),
  'calculationSettings.workTypes[].active': f('workTypeActive', S.earnings, T.bool),
  'calculationSettings.workTypes[].default': f('workTypeDefault', S.earnings, T.bool),
  'calculationSettings.workTypes[].dropdown': f('workTypeDropdown', S.earnings, T.bool),
  'calculationSettings.workTypes[].hrDropdown': f('workTypeHrDropdown', S.earnings, T.bool),
  'calculationSettings.workTypes[].confirmation': f('workTypeConfirmation', S.earnings, T.bool),
  'calculationSettings.workTypes[].createsDocument': f('workTypeCreatesDocument', S.earnings, T.bool),
  'calculationSettings.workTypes[].workType': f('workTypeIsWorkType', S.earnings, T.bool),
  'calculationSettings.workTypes[].parent': f('workTypeParent', S.earnings),
  'calculationSettings.workTypes[].color.argb': f('workTypeColor', S.earnings, T.color),
  'calculationSettings.workTypes[].textColor.argb': f('workTypeTextColor', S.earnings, T.color),

  'salaryCalculationSettings.salaryCalculationType': f(
    'salaryCalculationType',
    S.earnings,
    T.enum,
    'salaryCalculationType'
  ),
  'salaryCalculationSettings.holidayLeaveConfirmation': f('holidayLeaveConfirmation', S.earnings),
  'salaryCalculationSettings.paidLeaveConfirmation': f('paidLeaveConfirmation', S.earnings),
  'salaryCalculationSettings.publicHolidayConfirmation': f('publicHolidayConfirmation', S.earnings),
  'salaryCalculationSettings.sickLeaveUpTo30Days65Confirmation': f('sickLeave65Confirmation', S.earnings),
  'salaryCalculationSettings.sickLeaveUpTo30Days100Confirmation': f('sickLeave100Confirmation', S.earnings),

  'applicationSettings.isHr': f('moduleHr', S.settings, T.bool),
  'applicationSettings.isSector': f('moduleSector', S.settings, T.bool),
  'applicationSettings.isForEmployees': f('moduleForEmployees', S.settings, T.bool),
  'applicationSettings.eInvoice': f('moduleEInvoice', S.settings, T.bool),
  'applicationSettings.timeTracking': f('moduleTimeTracking', S.settings, T.bool),
  'applicationSettings.assistantEnabled': f('moduleAssistant', S.settings, T.bool),
  'applicationSettings.calculatorEnabled': f('moduleCalculator', S.settings, T.bool),
  'applicationSettings.salaryTileEnabled': f('moduleSalaryTile', S.settings, T.bool),
  'applicationSettings.activityFeedEnabled': f('moduleActivityFeed', S.settings, T.bool),
  'applicationSettings.mfaEnabled': f('mfaEnabled', S.access, T.bool),
  'applicationSettings.holidayDaysTracker': f('holidayDaysTracker', S.holiday, T.bool),
  'applicationSettings.strictOldHolidayCutoff': f('strictOldHolidayCutoff', S.holiday, T.bool),
  'applicationSettings.holidayFirstPartRequired': f('holidayFirstPartRequired', S.holiday, T.bool),
  'applicationSettings.holidayFirstPartWarnings': f('holidayFirstPartWarnings', S.holiday, T.bool),
  'applicationSettings.weekendLeaves': f('weekendLeaves', S.holiday, T.bool),
  'applicationSettings.trackerStartDate': f('trackerStartDate', S.holiday, T.date),
  'applicationSettings.foreignWorkPermitTracker': f('foreignWorkPermitTracker', S.employment, T.bool),
  'applicationSettings.isSickLeaveRequest': f('sickLeaveRequest', S.settings, T.bool),
  'applicationSettings.isHrOfficerSelfApprove': f('hrOfficerSelfApprove', S.access, T.bool),
  'applicationSettings.companyAdminsReceiveHREmails': f('adminsReceiveHrEmails', S.settings, T.bool),
  'applicationSettings.isGrossBonus': f('grossBonus', S.earnings, T.bool),
  'applicationSettings.qrInEInvoices': f('qrInEInvoices', S.settings, T.bool),
  'applicationSettings.showInternalRefNumber': f('showInternalRefNumber', S.settings, T.bool),
  'applicationSettings.showCompanyLogoInDocs': f('showCompanyLogoInDocs', S.documents, T.bool),
  'applicationSettings.documentReturnType': f('documentReturnType', S.documents, T.enum, 'documentReturnType'),
  'applicationSettings.documentSeparateRequestResponse': f('documentSeparateRequestResponse', S.documents, T.bool),
  'applicationSettings.selectedSortingType': f('sortingType', S.settings, T.enum, 'sortingType'),
  'applicationSettings.typeOfCompany': f('typeOfCompany', S.settings, T.enum, 'typeOfCompany'),
  'applicationSettings.typeOfHr': f('typeOfHr', S.settings, T.enum, 'typeOfHr'),
  // Measured on production: 161 companies hold 'none' and 5 hold a number of
  // days. Only the word needs saying; a number says itself.
  'applicationSettings.workFromHomeType': f(
    'workFromHomeType',
    S.settings,
    T.enum,
    'workFromHomeType'
  ),
  'applicationSettings.timesheetReview.enabled': f('timesheetReviewEnabled', S.settings, T.bool),
  'applicationSettings.timesheetReview.rules.maxHoursPerDay': f('maxHoursPerDay', S.settings, T.hours),
  'applicationSettings.timesheetReview.rules.flagPartialPublicHoliday': f('flagPartialPublicHoliday', S.settings, T.bool),
  'applicationSettings.timesheetReview.rules.flagUnmarkedOvertime': f('flagUnmarkedOvertime', S.settings, T.bool),
  'applicationSettings.timesheetReview.rules.flagOTSameDayAsPublicHoliday': f('flagOTSameDayAsPublicHoliday', S.settings, T.bool),
  'applicationSettings.timesheetReview.rules.flagNightWithRegularEqFond': f('flagNightWithRegularEqFond', S.settings, T.bool),
  'applicationSettings.toolsEnabled': f('moduleTools', S.settings, T.bool),
  'applicationSettings.ssoTenantId': f('ssoTenantId', S.access, T.text),
  'applicationSettings.invoiceProjectEnabled': f('invoiceProjectEnabled', S.settings, T.bool),
  'applicationSettings.invoiceJournalEnabled': f('invoiceJournalEnabled', S.settings, T.bool),
  'applicationSettings.salesJournalEnabled': f('salesJournalEnabled', S.settings, T.bool),
  'applicationSettings.vatNoticeEnabled': f('vatNoticeEnabled', S.settings, T.bool),
  'applicationSettings.advancedSalesInvoicingEnabled': f('advancedSalesInvoicingEnabled', S.settings, T.bool),
  'applicationSettings.manualPurchaseInvoiceEnabled': f('manualPurchaseInvoiceEnabled', S.settings, T.bool),
  'applicationSettings.autoArchiveInvoicesEnabled': f('autoArchiveInvoices', S.settings, T.bool),
  'applicationSettings.autoArchiveAfterMonths': f('autoArchiveAfterMonths', S.settings, T.number),
  'applicationSettings.manualArchiveInvoicesEnabled': f('manualArchiveInvoices', S.settings, T.bool),
  'applicationSettings.postedToErpTrackingEnabled': f('postedToErpTracking', S.settings, T.bool),
  'applicationSettings.taxReturnsClientView': f('taxReturnsClientView', S.settings, T.bool),
  'applicationSettings.ledgerCardsEnabled': f('ledgerCardsEnabled', S.settings, T.bool),
  'applicationSettings.sefCustomsEnabled': f('sefCustomsEnabled', S.settings, T.bool),
  'applicationSettings.sefInputVatEnabled': f('sefInputVatEnabled', S.settings, T.bool),
  'applicationSettings.sefVatRecordingEnabled': f('sefVatRecordingEnabled', S.settings, T.bool),
  'applicationSettings.sefFiscalBillsEnabled': f('sefFiscalBillsEnabled', S.settings, T.bool),

  'timeTrackingSettings.allowTimer': f('allowTimer', S.settings, T.bool),
  'timeTrackingSettings.requireApproval': f('requireApproval', S.settings, T.bool),
  'timeTrackingSettings.lockAfterApproval': f('lockAfterApproval', S.settings, T.bool),
  'timeTrackingSettings.defaultBillable': f('defaultBillable', S.settings, T.bool),
  'timeTrackingSettings.autoSyncToPayroll': f('autoSyncToPayroll', S.settings, T.bool),
  'timeTrackingSettings.maxHoursPerDay': f('maxHoursPerDay', S.settings, T.hours),
  'timeTrackingSettings.attendanceMode': f('attendanceMode', S.settings, T.bool),

  'loyaltyHolidaySettings.enabled': f('loyaltyHolidayEnabled', S.holiday, T.bool),
  'loyaltyHolidaySettings.thresholds': f('loyaltyHolidayThresholds', S.holiday, T.array),
  'loyaltyHolidaySettings.thresholds[].years': f('loyaltyThresholdYears', S.holiday, T.number),
  'loyaltyHolidaySettings.thresholds[].days': f('loyaltyThresholdDays', S.holiday, T.days),

  'bonusVariations.enabled': f('bonusVariationsEnabled', S.earnings, T.bool),
  'bonusVariations.types': f('bonusVariationTypes', S.earnings, T.array),
  'suspensionVariations.enabled': f('suspensionVariationsEnabled', S.earnings, T.bool),
  'suspensionVariations.types': f('suspensionVariationTypes', S.earnings, T.array),
  'nonMonetaryBenefitVariations.enabled': f('nonMonetaryVariationsEnabled', S.earnings, T.bool),
  'nonMonetaryBenefitVariations.types': f('nonMonetaryVariationTypes', S.earnings, T.array),

  'clientsAdmins.clients': f('clients', S.access, T.array),
  'clientsAdmins.companyAdmins': f('companyAdmins', S.access, T.array),
  'clientsAdmins.clientOfficers': f('clientOfficers', S.access, T.array),
  'clientsAdmins.eInvoiceOfficers': f('eInvoiceOfficers', S.access, T.array),

  'authorizedRepresentatives.authorizedPerson': f('authorizedPerson', S.documents),
  'authorizedRepresentatives.authorizedPersonJmbg': f('authorizedPersonJmbg', S.documents, T.sensitive),
  'authorizedRepresentatives.signature': f('signature', S.documents),

  ownershipStructure: f('ownershipStructure', S.settings, T.array),
  'ownershipStructure[].name': f('ownerName', S.settings),
  'ownershipStructure[].JMBG': f('ownerJmbg', S.settings),
  'ownershipStructure[].type': f('ownerType', S.settings, T.enum, 'ownerType'),
  'ownershipStructure[].percentageOfOwnership': f('ownershipPercentage', S.settings, T.percent),
  'ownershipStructure[].dateOfBirth': f('ownerBirthDate', S.settings, T.date),
  'ownershipStructure[].placeOfBirth': f('ownerBirthPlace', S.settings),
  'ownershipStructure[].countryOfBirth': f('ownerBirthCountry', S.settings),
  'ownershipStructure[].addressOfResidence': f('ownerAddress', S.settings),
  'ownershipStructure[].placeOfResidence': f('ownerPlace', S.settings),
  'ownershipStructure[].countryOfResidence': f('ownerCountry', S.settings),
  'ownershipStructure[].typeOfDocument': f('ownerDocumentType', S.settings),
  'ownershipStructure[].numberOfDocument': f('ownerDocumentNumber', S.settings),
  'ownershipStructure[].dateOfIdDocIssue': f('ownerDocumentDate', S.settings, T.date),
  'ownershipStructure[].placeOfIdDocIssue': f('ownerDocumentPlace', S.settings),
  'ownershipStructure[].issuerOfDocument': f('ownerDocumentIssuer', S.settings),
  'ownershipStructure[].signature': f('ownerSignature', S.settings),
  'ownershipStructure[].useSignatureOnDocs': f('ownerUseSignature', S.settings, T.bool),

  'companyDocsSettings.holidayLeave': f('docsHolidayLeave', S.documents, T.bool),
  'companyDocsSettings.holidayLeaveEmail': f('docsHolidayLeaveEmail', S.documents, T.bool),
  'companyDocsSettings.paidLeave': f('docsPaidLeave', S.documents, T.bool),
  'companyDocsSettings.paidLeaveEmail': f('docsPaidLeaveEmail', S.documents, T.bool),
  'companyDocsSettings.nonPaidLeave': f('docsNonPaidLeave', S.documents, T.bool),
  'companyDocsSettings.nonPaidLeaveEmail': f('docsNonPaidLeaveEmail', S.documents, T.bool),
  'companyDocsSettings.sickLeave': f('docsSickLeave', S.documents, T.bool),
  'companyDocsSettings.sickLeaveEmail': f('docsSickLeaveEmail', S.documents, T.bool),
  'companyDocsSettings.maternityLeave': f('docsMaternityLeave', S.documents, T.bool),
  'companyDocsSettings.maternityLeaveEmail': f('docsMaternityLeaveEmail', S.documents, T.bool),
  'companyDocsSettings.pregnancyLeave': f('docsPregnancyLeave', S.documents, T.bool),
  'companyDocsSettings.pregnancyLeaveEmail': f('docsPregnancyLeaveEmail', S.documents, T.bool),
  'companyDocsSettings.bonus': f('docsBonus', S.documents, T.bool),
  'companyDocsSettings.bonusEmail': f('docsBonusEmail', S.documents, T.bool),
  'companyDocsSettings.suspension': f('docsSuspension', S.documents, T.bool),
  'companyDocsSettings.suspensionEmail': f('docsSuspensionEmail', S.documents, T.bool),
  'companyDocsSettings.workFromHomeEmail': f('docsWorkFromHomeEmail', S.documents, T.bool),
  'companyDocsSettings.sendRequestEmailsToEmployees': f('docsSendToEmployees', S.documents, T.bool),
  'companyDocsSettings.showMemorandumInDocs': f('docsShowMemorandum', S.documents, T.bool),
  'companyDocsSettings.showSignatureOnDocs': f('docsShowSignature', S.documents, T.bool),
  'companyDocsSettings.hideEmployeeSignature': f('docsHideEmployeeSignature', S.documents, T.bool),
  'companyDocsSettings.additionalHolidayDays': f('docsAdditionalHolidayDays', S.documents, T.bool),
  'companyDocsSettings.additionalHolidayDaysEmail': f('docsAdditionalHolidayDaysEmail', S.documents, T.bool),

  'companyPaymentsSlips.paymentSlips': f('paymentSlips', S.settings, T.array),
  'companyPaymentsSlips.paymentSlipsEmailReceivers': f('paymentSlipsEmailReceivers', S.settings, T.array),
  'questionnaireData.questionnarieDocs': f('questionnaireDocs', S.documents, T.array),
};

/* ------------------------------------------------------------------ *
 * User
 * ------------------------------------------------------------------ */
const USER = {
  firstName: f('firstName', S.personal),
  lastName: f('lastName', S.personal),
  jmbg: f('personalNumber', S.personal),
  legalId: f('legalId', S.personal),
  avatar: f('avatar', S.personal),
  email: f('email', S.contact),
  businessPhone: f('businessPhone', S.contact),
  privatePhone: f('privatePhone', S.contact),
  address: f('address', S.contact),
  city: f('city', S.contact),
  country: f('country', S.contact),
  status: f('status', S.access, T.enum, 'status'),
  roles: f('roles', S.access, T.array),
  companies: f('companies', S.access, T.array),
  employeeId: f('linkedEmployee', S.access, T.ref),
  sectorId: f('sector', S.access, T.ref),
  context: f('context', S.access),
  mfaEnabled: f('mfaEnabled', S.access, T.bool),
  statuaryAuthorization: f('statuaryAuthorization', S.access, T.bool),
  canSelfApproveRequests: f('canSelfApproveRequests', S.access, T.bool),
  restrictedPermissions: f('restrictedPermissions', S.access, T.array),
  // The hash itself is redacted before it leaves the server; what remains is
  // the fact that the password was changed, which is exactly what a trail is
  // read for.
  password: f('password', S.access, T.sensitive),
  passwordChangedAt: f('passwordChangedAt', S.access, T.datetime),
  passwordToken: f('passwordToken', S.access, T.sensitive),
  passwordTokenExpiresAt: f('passwordTokenExpiresAt', S.access, T.datetime),
  ssoExempt: f('ssoExempt', S.access, T.bool),
  'avatar.key': f('avatarKey', S.personal),
  'avatar.location': f('avatarLocation', S.personal),
  'avatar.originalName': f('avatarName', S.personal),
  'avatar.mimetype': f('avatarType', S.personal),
  'adminEmailsSettings.leavesDecisions': f('emailLeavesDecisions', S.settings, T.bool),
  'adminEmailsSettings.leavesDecisionsHrOfficers': f('emailLeavesDecisionsHr', S.settings, T.bool),
  'adminEmailsSettings.newEmployeeEmails': f('emailNewEmployee', S.settings, T.bool),
  'adminEmailsSettings.officialTravelEmails': f('emailOfficialTravel', S.settings, T.bool),
  'adminEmailsSettings.paymentOrdersEmails': f('emailPaymentOrders', S.settings, T.bool),
  'adminEmailsSettings.terminationExtensionEmails': f('emailTerminationExtension', S.settings, T.bool),
  'adminEmailsSettings.workPermissionExpireEmails': f('emailWorkPermission', S.settings, T.bool),
  'adminEmailsSettings.timesheetSubmission': f('emailTimesheetSubmission', S.settings, T.bool),
  'adminEmailsSettings.receiveLeaveCalendarInvites': f('emailLeaveCalendarInvites', S.settings, T.bool),
  // Which of the two leave-decision switches the form had selected. Written
  // alongside them by the email settings form, so the trail carries it.
  'adminEmailsSettings.emailRadioOption': f('emailLeavesDecisionsChoice', S.settings),
};

/* ------------------------------------------------------------------ *
 * Sector
 * ------------------------------------------------------------------ */
const SECTOR = {
  sectorName: f('sectorName', S.settings),
  sectorDescription: f('sectorDescription', S.settings),
  sectorAdditionalDetails: f('sectorAdditionalDetails', S.settings),
  status: f('status', S.system, T.enum, 'status'),
  company: f('company', S.settings, T.ref),
  emailReceivers: f('emailReceivers', S.contact, T.array),
  hrSpecialists: f('hrSpecialists', S.access, T.array),
};

/* ------------------------------------------------------------------ *
 * Salary
 * ------------------------------------------------------------------ */
const SALARY = {
  'currencies.mealAllowanceCurrency': f('mealAllowanceCurrency', S.earnings, T.text),
  'currencies.holidayAllowanceCurrency': f('holidayAllowanceCurrency', S.earnings, T.text),
  'currencies.travelAllowanceCurrency': f('travelAllowanceCurrency', S.earnings, T.text),
  'currencies.otherIncomeCurrency': f('otherIncomeCurrency', S.earnings, T.text),
  'currencies.otherExpensesCurrency': f('otherExpensesCurrency', S.earnings, T.text),
  'currencies.bonusCurrency': f('bonusCurrency', S.earnings, T.text),
  'currencies.suspensionCurrency': f('suspensionCurrency', S.earnings, T.text),
  'currencies.workFromHomeAllowanceCurrency': f('workFromHomeAllowanceCurrency', S.earnings, T.text),
  'currencies.nonMonetaryBenefitCurrency': f('nonMonetaryBenefitCurrency', S.earnings, T.text),
  status: f('status', S.earnings, T.enum, 'salaryStatus'),
  mealAllowance: f('mealAllowance', S.earnings, T.money),
  holidayAllowance: f('holidayAllowance', S.earnings, T.money),
  travelAllowance: f('travelAllowance', S.earnings, T.money),
  workFromHomeAllowance: f('workFromHomeAllowance', S.earnings, T.money),
  bonus: f('bonus', S.earnings, T.money),
  otherIncome: f('otherIncome', S.earnings, T.money),
  otherExpenses: f('otherExpenses', S.earnings, T.money),
  suspension: f('suspension', S.earnings, T.money),
  nonMonetaryBenefit: f('nonMonetaryBenefit', S.earnings, T.money),
  nonMonetaryBenefitReason: f('nonMonetaryBenefitReason', S.earnings),
  comment: f('comment', S.earnings),
  // The variation maps themselves; their per-code entries are matched by
  // pattern below, because the key is a company's own code, not a field name.
  bonusVariationsData: f('bonusVariations', S.earnings),
  suspensionVariationsData: f('suspensionVariations', S.earnings),
  nonMonetaryBenefitVariationsData: f('nonMonetaryBenefitVariations', S.earnings),
  attachments: f('attachments', S.documents, T.array),
  'attachments[].originalName': f('attachmentName', S.documents),
  'attachments[].type': f('attachmentType', S.documents),
  hoursByDay: f('hoursByDay', S.earnings, T.array),
  'hoursByDay[].selectedDate': f('day', S.earnings, T.date),
  'hoursByDay[].hours': f('hours', S.earnings, T.hours),
  'hoursByDay[].workType': f('workType', S.earnings),
  'hoursByDay[].comment': f('comment', S.earnings),
};

/* ------------------------------------------------------------------ *
 * Request — not opened from the drawer today, but its rows are the most
 * numerous action in the system and Phase D will read them.
 * ------------------------------------------------------------------ */
const REQUEST = {
  requestId: f('requestId', S.system, T.text),
  documentId: f('documentId', S.documents, T.ref),
  typeOfFile: f('typeOfFile', S.documents, T.text),
  originalName: f('attachmentName', S.documents, T.text),
  location: f('fileLocation', S.documents, T.text),
  'externalRef.source': f('externalRefSource', S.system, T.text),
  'externalRef.id': f('externalRefId', S.system, T.text),
  'travelOrder.travelOrderNo': f('travelOrderNo', S.other, T.text),
  'holidayDaysCalculation.holidayDaysYear': f('holidayDaysYear', S.holiday, T.number),
  'holidayDaysCalculation.numberOfHolidayDays': f('holidayDaysTotal', S.holiday, T.days),
  'holidayDaysCalculation.numberOfHolidayDaysPast': f('holidayDaysPast', S.holiday, T.days),
  'holidayDaysCalculation.numberOfHolidayDaysCurrent': f('holidayDaysCurrent', S.holiday, T.days),
  'holidayDaysCalculation.numberOfHolidayDaysContract': f('holidayDaysContract', S.holiday, T.days),
  'holidayDaysCalculation.numberOfHolidayDaysByEndOfContract': f('holidayDaysByEndOfContract', S.holiday, T.days),
  'holidayDaysCalculation.numberOfHolidayDaysUsed': f('holidayDaysUsed', S.holiday, T.days),
  'holidayDaysCalculation.numberOfAdditionalHolidayDays': f('numberOfAdditionalHolidayDays', S.holiday, T.days),
  'holidayDaysCalculation.holidayLeaveForYear': f('holidayLeaveForYear', S.holiday, T.number),
  'holidayDaysCalculation.isBothYearsHoliday': f('isBothYearsHoliday', S.holiday, T.bool),
  'holidayDaysCalculation.pastDaysYear': f('pastDaysYear', S.holiday, T.number),
  'holidayDaysCalculation.currentDaysYear': f('currentDaysYear', S.holiday, T.number),
  'holidayDaysCalculation.numberOfNewDaysConsumed': f('numberOfNewDaysConsumed', S.holiday, T.days),
  'holidayDaysCalculation.usedPastDays': f('usedPastDays', S.holiday, T.days),
  'holidayDaysCalculation.usedCurrentDays': f('usedCurrentDays', S.holiday, T.days),
  status: f('status', S.system, T.enum, 'requestStatus'),
  employee: f('employee', S.employment, T.ref),
  company: f('company', S.employment, T.ref),
  firstName: f('firstName', S.personal),
  lastName: f('lastName', S.personal),
  email: f('email', S.contact),
  dateFrom: f('dateFrom', S.holiday, T.date),
  dateTo: f('dateTo', S.holiday, T.date),
  // Written onto the request itself when the leave is approved
  // (`request.pipeline.accept.js`), alongside the attachment payload — the
  // audit trail therefore carries them as request fields. They repeat the
  // period `dateFrom`/`dateTo` already state, so they are labelled apart
  // rather than twice the same, and the reader sees which pair is which.
  startDate: f('leaveStartDate', S.holiday, T.date),
  endDate: f('leaveEndDate', S.holiday, T.date),
  firstDayBack: f('firstDayBack', S.holiday, T.date),
  numOfDays: f('numOfDays', S.holiday, T.days),
  typeOfLeave: f('typeOfLeave', S.holiday),
  reasonOfLeave: f('reasonOfLeave', S.holiday),
  reasonOfLeaveEng: f('reasonOfLeaveEng', S.holiday),
  reasonRejection: f('reasonRejection', S.holiday),
  yearStart: f('yearStart', S.holiday, T.number),
  yearEnd: f('yearEnd', S.holiday, T.number),
  holidayDaysCalculation: f('holidayDaysCalculation', S.holiday),
  lawWarningsAcknowledged: f('lawWarningsAcknowledged', S.holiday, T.array),
  // Both are sent with the request rather than stored on it, and both say the
  // same kind of thing: somebody was warned and went ahead anyway.
  override: f('override', S.holiday, T.bool),
  overrideLawWarning: f('overrideLawWarning', S.holiday, T.bool),
  lawWarningReason: f('lawWarningReason', S.holiday),
  dateAccepted: f('dateAccepted', S.system, T.date),
  // Stored as epoch milliseconds in a string ("1725148800000"); only the
  // dictionary type makes it render as a date.
  dateOfCreation: f('dateOfCreation', S.system, T.date),
  title: f('title', S.documents),
  dateOvertime: f('dateOvertime', S.earnings, T.date),
  basisOvertime: f('basisOvertime', S.earnings),
  basisOvertimeEng: f('basisOvertimeEng', S.earnings),
  numOfHoursOvertime: f('numOfHoursOvertime', S.earnings, T.hours),
  percentageBonusOvertime: f('percentageBonusOvertime', S.earnings, T.percent),
  typeBonus: f('typeBonus', S.earnings),
  typeBonusEng: f('typeBonusEng', S.earnings),
  valueBonus: f('valueBonus', S.earnings, T.money),
  currencyBonus: f('currencyBonus', S.earnings),
  bonusMonth: f('bonusMonth', S.earnings),
  bonusVariationType: f('bonusVariationType', S.earnings),
  bonusVariationLabel: f('bonusVariationLabel', S.earnings),
  valueSuspension: f('valueSuspension', S.earnings, T.money),
  currencySuspension: f('currencySuspension', S.earnings),
  suspensionMonth: f('suspensionMonth', S.earnings),
  suspensionVariationType: f('suspensionVariationType', S.earnings),
  suspensionVariationLabel: f('suspensionVariationLabel', S.earnings),
  documentName: f('documentName', S.documents),
  documentNamePdf: f('documentNamePdf', S.documents),
};

/* ------------------------------------------------------------------ *
 * Electronic invoices (sales and purchase)
 *
 * The feed shows these to every company on e-invoicing, and until now
 * every one of their fields printed as a raw path: `archived`,
 * `approvedBy`, `transactionDescription`. Both directions share a shape,
 * so both share this.
 * ------------------------------------------------------------------ */
const ELECTRONIC_INVOICE = {
  archived: f('archived', S.system, T.bool),
  archivedDate: f('archivedDate', S.system, T.datetime),
  approvedAt: f('approvedAt', S.system, T.datetime),
  approvedBy: f('approvedBy', S.access, T.ref),
  approvedSource: f('approvedSource', S.system, T.text),
  project: f('project', S.other, T.ref),
  transactionDescription: f('transactionDescription', S.other, T.text),
  status: f('status', S.system, T.text),
  internalReferenceNumber: f('internalReferenceNumber', S.other, T.text),
  recovered: f('recovered', S.system, T.bool),
  // Who refused the invoice and from where. `rejectedSource` is the only thing
  // that tells a deliberate refusal from one SEF took by itself when nobody
  // acted in time, so it is worth a line of its own.
  rejectedAt: f('rejectedAt', S.system, T.datetime),
  rejectedBy: f('rejectedBy', S.access, T.ref),
  rejectedSource: f('rejectedSource', S.system, T.enum, 'rejectedSource'),
  reasonOfRejection: f('reasonOfRejection', S.other, T.text),
  rejectOrStornoComment: f('rejectOrStornoComment', S.other, T.text),
  // When the supplier cancelled it at SEF, and the same moment again when it
  // had already been approved by then.
  stornoAt: f('stornoAt', S.system, T.datetime),
  stornoAfterApprovalAt: f('stornoAfterApprovalAt', S.system, T.datetime),
};

/* ------------------------------------------------------------------ *
 * Regular invoice — the ones written on the platform rather than
 * fetched from SEF.
 * ------------------------------------------------------------------ */
const REGULAR_INVOICE = {
  status: f('status', S.system, T.text),
  documentType: f('documentType', S.other, T.text),
  documentNumber: f('documentNumber', S.other, T.text),
  documentDate: f('documentDate', S.other, T.date),
  supplyDate: f('supplyDate', S.other, T.date),
  dueDate: f('dueDate', S.other, T.date),
  documentCurrency: f('documentCurrency', S.other, T.text),
  documentLanguage: f('documentLanguage', S.other, T.text),
  poNumber: f('poNumber', S.other, T.text),
  comment: f('comment', S.other),
  total: f('total', S.other, T.money),
  totalDiscounts: f('totalDiscounts', S.other, T.money),
  totalVatExcl: f('totalVatExcl', S.other, T.money),
  vats: f('vats', S.other, T.array),
  prepaidAmount: f('prepaidAmount', S.other, T.money),
  openBalance: f('openBalance', S.other, T.money),
  paymentReference: f('paymentReference', S.other, T.text),
  internalReferenceNumber: f('internalReferenceNumber', S.other, T.text),
  archived: f('archived', S.system, T.bool),
  archivedDate: f('archivedDate', S.system, T.datetime),
  recovered: f('recovered', S.system, T.bool),
  customer: f('customer', S.other, T.ref),
  customerId: f('customerId', S.other, T.text),
  customerName: f('customerName', S.other),
  customerVat: f('customerVat', S.other),
  customerCompanyNumber: f('customerCompanyNumber', S.other),
  customerPostalAddress: f('customerPostalAddress', S.contact),
  customerCity: f('customerCity', S.contact),
  customerZipCode: f('customerZipCode', S.contact),
  customerCountry: f('customerCountry', S.contact),
  supplierName: f('supplierName', S.other),
  supplierVat: f('supplierVat', S.other),
  supplierCompanyNumber: f('supplierCompanyNumber', S.other),
  products: f('invoiceProducts', S.other, T.array),
  'products[].description': f('description', S.other),
  'products[].quantity': f('quantity', S.other, T.number),
  'products[].unit': f('unit', S.other),
  'products[].ratePrice': f('ratePrice', S.other, T.money),
  'products[].vat': f('vat', S.other),
  'products[].discount': f('discount', S.other),
  'products[].subtotal': f('subtotal', S.other, T.money),
  'products[].grossPrice': f('grossPrice', S.other, T.money),
  uploadedFiles: f('uploadedFiles', S.documents, T.array),
  'uploadedFiles[].originalName': f('attachmentName', S.documents),
  'uploadedFiles[].location': f('attachmentLocation', S.documents),
  'uploadedFiles[].mimetype': f('attachmentType', S.documents),
  'uploadedFiles[].size': f('attachmentSize', S.documents),
  pdf: f('pdf', S.documents, T.text),
  unifiedPdf: f('unifiedPdf', S.documents, T.text),
  sefPdf: f('sefPdf', S.documents, T.text),
  isForeignInvoice: f('isForeignInvoice', S.other, T.bool),
  includeLogoInInvoice: f('includeLogoInInvoice', S.documents, T.bool),
  project: f('project', S.other, T.ref),
  vehicleId: f('vehicle', S.other, T.ref),
};

/* ------------------------------------------------------------------ *
 * Time entry — the whole module, none of whose fields had an entry
 * before: measured on production it is the single largest source of
 * raw paths in the feed.
 * ------------------------------------------------------------------ */
const TIME_ENTRY = {
  employee: f('employee', S.employment, T.ref),
  createdFor: f('createdFor', S.employment, T.ref),
  project: f('project', S.other, T.ref),
  task: f('task', S.other, T.ref),
  date: f('date', S.earnings, T.date),
  entryType: f('entryType', S.earnings, T.enum, 'entryType'),
  status: f('status', S.earnings, T.enum, 'timeEntryStatus'),
  duration: f('duration', S.earnings, T.number),
  startTime: f('startTime', S.earnings, T.datetime),
  endTime: f('endTime', S.earnings, T.datetime),
  billable: f('billable', S.earnings, T.bool),
  hourlyRate: f('hourlyRate', S.earnings, T.money),
  description: f('description', S.earnings),
  autoGenerated: f('autoGenerated', S.system, T.bool),
  approvedBy: f('approvedBy', S.access, T.ref),
  approvedAt: f('approvedAt', S.system, T.datetime),
  rejectionReason: f('rejectionReason', S.system),
  reason: f('reason', S.system),
  timerActive: f('timerActive', S.earnings, T.bool),
  timerStartedAt: f('timerStartedAt', S.earnings, T.datetime),
  timerPaused: f('timerPaused', S.earnings, T.bool),
  timerPausedAt: f('timerPausedAt', S.earnings, T.datetime),
  totalPausedMs: f('totalPausedMs', S.earnings, T.number),
  syncedToSalary: f('syncedToSalary', S.earnings, T.bool),
  salaryId: f('salary', S.earnings, T.ref),
  // Written only by the bulk approve/submit path, which logs one row for the
  // whole batch rather than one per entry.
  action: f('action', S.system, T.text),
  entryCount: f('entryCount', S.system, T.number),
};

/* ------------------------------------------------------------------ *
 * Guideline, category, customer, product, support
 * ------------------------------------------------------------------ */
const GUIDELINE = {
  category: f('category', S.other, T.ref),
  type: f('guidelineType', S.other, T.enum, 'guidelineType'),
  title: f('title', S.other),
  link: f('link', S.other),
  file: f('file', S.documents),
  'file.key': f('fileKey', S.documents),
  'file.location': f('fileLocation', S.documents),
  'file.originalName': f('attachmentName', S.documents),
  'file.mimetype': f('attachmentType', S.documents),
  companyVisibility: f('companyVisibility', S.access),
  'companyVisibility.mode': f('companyVisibilityMode', S.access, T.enum, 'visibilityMode'),
  'companyVisibility.companies': f('companyVisibilityCompanies', S.access, T.array),
};

const CUSTOMER = {
  customerName: f('customerName', S.other),
  vatRegistrationCode: f('customerVat', S.other),
  companyNumber: f('customerCompanyNumber', S.other),
  bugetCompanyNumber: f('budgetCompanyNumber', S.other),
  contactPerson: f('contactPerson', S.contact),
  phoneNumber: f('phone', S.contact),
  postalAddress: f('postalAddress', S.contact),
  zipCode: f('zip', S.contact),
  userCompany: f('company', S.employment, T.ref),
  // Written by the invoice form when it creates a customer on the fly.
  from: f('createdFrom', S.system, T.text),
};

const PRODUCT = {
  title: f('title', S.other),
  description: f('description', S.other),
  unit: f('unit', S.other),
  tax: f('tax', S.other),
  priceEUR: f('priceEUR', S.other, T.money),
  priceRSD: f('priceRSD', S.other, T.money),
};

// The platform-only contact block of a supplier (Suppliers → profile → Data).
const SUPPLIER_NOTE = {
  contactPerson: f('contactPerson', S.contact),
  email: f('email', S.contact),
  phone: f('phone', S.contact),
  // The server writes the follower's name into the audit entry, not the id.
  followedByName: f('supplierFollowedBy', S.contact),
  followedBy: f('supplierFollowedBy', S.contact, T.ref),
  note: f('comment', S.other),
  pib: f('pib', S.other),
  updatedBy: f('modifiedBy', S.system, T.ref),
};

const SUPPORT = {
  subject: f('subject', S.other),
  message: f('message', S.other),
  allMessages: f('allMessages', S.other, T.array),
  'allMessages[].message': f('message', S.other),
  'allMessages[].user': f('messageAuthor', S.other, T.ref),
  'allMessages[].createdAt': f('createdAt', S.system, T.datetime),
  ticketNumber: f('ticketNumber', S.other, T.number),
  type: f('supportType', S.other, T.enum, 'supportType'),
  status: f('status', S.system, T.enum, 'supportStatus'),
  user: f('createdBy', S.access, T.ref),
  companies: f('companies', S.access, T.array),
};

const BY_MODEL = {
  Employee: EMPLOYEE,
  Company: COMPANY,
  User: USER,
  Sector: SECTOR,
  Salary: SALARY,
  Request: REQUEST,
  ElectronicPurchaseInvoice: ELECTRONIC_INVOICE,
  ElectronicSalesInvoice: ELECTRONIC_INVOICE,
  RegularInvoice: REGULAR_INVOICE,
  TimeEntry: TIME_ENTRY,
  Guideline: GUIDELINE,
  Customer: CUSTOMER,
  Product: PRODUCT,
  Support: SUPPORT,
  SupplierNote: SUPPLIER_NOTE,
};

/**
 * Paths whose middle segment is data rather than a field name.
 *
 * Bonuses, suspensions and non-monetary benefits are stored as a map keyed by
 * the company's own variation code — `bonusVariationsData.rad_od_kuce.amount`.
 * No fixed dictionary can list those keys, and on production they are the
 * second largest source of raw paths after time tracking. Each pattern names
 * the family and hands the code back as a parameter, so the line reads
 * "Bonus — rad_od_kuce · iznos" instead of the stored path.
 *
 * Ordered: first match wins, and every pattern is anchored so a longer, exact
 * entry in the model map is never displaced.
 */
const VARIATION_DETAILS = {
  amount: { i18nKey: 'amount', type: T.money },
  currency: { i18nKey: 'currency', type: T.text },
  dateOfCreation: { i18nKey: 'dateOfCreation', type: T.date },
  label: { i18nKey: 'variationLabel', type: T.text },
  validFrom: { i18nKey: 'validFrom', type: T.date },
  validTo: { i18nKey: 'validTo', type: T.date },
};

/**
 * Builds the descriptor for one entry of a variation map.
 *
 * @param {string} i18nKey - Family key ("bonusVariation", …).
 * @param {string} code - The company's variation code, shown as stored.
 * @param {string} [detail] - Leaf inside the entry, when the change is one field of it.
 * @param {string} [fallbackType] - Type for an entry that holds a bare value.
 * @returns {Object} Descriptor in the same shape as `f`.
 */
const variation = (i18nKey, code, detail, fallbackType = T.money) => {
  const known = detail ? VARIATION_DETAILS[detail] : null;

  return {
    i18nKey,
    section: S.earnings,
    type: known ? known.type : detail ? T.text : fallbackType,
    enumSet: undefined,
    params: { code },
    // An unrecognised leaf keeps its own name rather than being swallowed.
    detailKey: known ? known.i18nKey : detail || null,
    detailRaw: known ? null : detail || null,
  };
};

const PATTERNS = {
  Salary: [
    ['bonusVariationsData', 'bonusVariation'],
    ['suspensionVariationsData', 'suspensionVariation'],
    ['nonMonetaryBenefitVariationsData', 'nonMonetaryBenefitVariation'],
  ],
  Employee: [
    ['otherAllowances.bonusVariations', 'bonusVariation'],
    ['otherAllowances.suspensionVariations', 'suspensionVariation'],
    ['otherAllowances.nonMonetaryBenefitVariations', 'nonMonetaryBenefitVariation'],
    ['otherAllowances.suspensionVariationsDates', 'suspensionVariationPeriod'],
  ],
};

/**
 * Matches a path against the variation patterns of its model.
 *
 * @param {string} modelName - Model the record belongs to.
 * @param {string} path - Normalised field path.
 * @returns {Object|null} Descriptor, or null when no pattern applies.
 */
const matchPattern = (modelName, path) => {
  for (const [root, i18nKey] of PATTERNS[modelName] || []) {
    if (!path.startsWith(`${root}.`)) continue;

    const [code, ...tail] = path.slice(root.length + 1).split('.');
    if (!code) continue;

    return variation(
      i18nKey,
      code,
      tail.length ? tail.join('.') : undefined,
      i18nKey === 'suspensionVariationPeriod' ? T.text : T.money
    );
  }
  return null;
};

/**
 * Every translation key the dictionary can ask for.
 *
 * A key with no entry in a locale file renders as the raw stored path, which is
 * the one failure this dictionary exists to prevent and the one a reader
 * notices immediately. Exported so a test can hold the locale files to it
 * rather than waiting for somebody to spot `holidayDaysCalculation.numberOfHolidayDays`
 * in the feed.
 */
export const I18N_KEYS = Object.freeze(
  Array.from(
    new Set([
      ...Object.values(BY_MODEL).flatMap((model) =>
        Object.values(model).map((entry) => entry.i18nKey)
      ),
      ...Object.values(COMMON).map((entry) => entry.i18nKey),
      ...Object.values(PATTERNS).flatMap((patterns) =>
        patterns.map(([, i18nKey]) => i18nKey)
      ),
      ...Object.values(VARIATION_DETAILS).map((detail) => detail.i18nKey),
    ])
  ).sort()
);

/**
 * Subtrees that hold a populated reference rather than data of the record being
 * viewed. A snapshot of one employee carries the whole company, sector and
 * author documents inline — around 300 of its 438 leaf paths — which buries the
 * ~140 fields that actually belong to the employee. These roots collapse to a
 * single line naming the referenced record.
 */
const REFERENCE_ROOTS = {
  Employee: [
    'company',
    'employeeSector',
    'createdBy',
    'modifiedBy',
    'userId',
    'supersededBy',
    'careerHistory[].companyFrom',
    'careerHistory[].companyTo',
    'careerHistory[].oldSector',
    'careerHistory[].newSector',
  ],
  Company: ['createdBy', 'modifiedBy', 'sectors[]'],
  User: ['createdBy', 'modifiedBy', 'employeeId', 'sectorId', 'companies[]'],
  Sector: ['company', 'createdBy', 'modifiedBy'],
  Salary: ['company', 'employee', 'createdBy', 'modifiedBy'],
  Request: ['company', 'employee', 'createdBy', 'modifiedBy'],
  TimeEntry: [
    'company',
    'employee',
    'createdFor',
    'createdBy',
    'modifiedBy',
    'approvedBy',
    'project',
    'salaryId',
  ],
  RegularInvoice: ['company', 'customer', 'createdBy', 'modifiedBy', 'project'],
  Guideline: ['category', 'createdBy', 'modifiedBy'],
  Customer: ['userCompany', 'createdBy', 'modifiedBy'],
  Product: ['company', 'createdBy', 'modifiedBy'],
  Support: ['user', 'createdBy', 'modifiedBy'],
  SupplierNote: ['company', 'updatedBy', 'modifiedBy', 'followedBy'],
  ElectronicPurchaseInvoice: ['company', 'approvedBy', 'rejectedBy', 'project'],
  ElectronicSalesInvoice: ['company', 'approvedBy', 'rejectedBy', 'project'],
};

/**
 * Fields carried on every record that the drawer shows in its own header, so
 * repeating them as change lines is noise.
 */
export const HEADER_FIELDS = Object.freeze([
  'modifiedBy',
  'createdBy',
  'updatedBy',
  '__v',
  '_id',
  'updatedAt',
]);

/**
 * Turns `workTypes[3].label` into `workTypes[].label` so one dictionary entry
 * covers every element of an array.
 *
 * @param {string} path - Field path as stored.
 * @returns {string}
 */
export const normalizeFieldPath = (path) =>
  String(path ?? '').replace(/\[\d+\]/g, '[]');

/**
 * Leaf names that mean the same thing wherever they appear. Used only when the
 * full path is unknown, so a mislabel cannot displace an explicit entry.
 */
const LEAF_FALLBACK = {
  firstName: COMMON.firstName,
  lastName: COMMON.lastName,
  email: COMMON.email,
  address: COMMON.address,
  city: COMMON.city,
  country: COMMON.country,
  zip: COMMON.zip,
  phone: COMMON.phone,
  createdAt: COMMON.createdAt,
  updatedAt: COMMON.updatedAt,
  createdBy: COMMON.createdBy,
  modifiedBy: COMMON.modifiedBy,
};

/**
 * Describes a field path for display.
 *
 * Resolution order: model entry, then shared entry, then a leaf-name fallback,
 * then the raw path. Nothing is ever dropped for being unknown — an unmapped
 * field shows under its stored name in the "other" section.
 *
 * @param {string} modelName - Model the record belongs to.
 * @param {string} path - Field path, with or without array indices.
 * @returns {{i18nKey: string|null, section: string, type: string, enumSet: string|undefined, path: string, known: boolean}}
 */
export const describeField = (modelName, path) => {
  const normalized = normalizeFieldPath(path);
  const model = BY_MODEL[modelName] || {};

  const hit = model[normalized] || COMMON[normalized];
  if (hit) return { ...hit, path: normalized, known: true };

  const pattern = matchPattern(modelName, normalized);
  if (pattern) return { ...pattern, path: normalized, known: true };

  const leaf = normalized.split('.').pop().replace('[]', '');
  const leafHit = LEAF_FALLBACK[leaf];
  if (leafHit) return { ...leafHit, path: normalized, known: true };

  return {
    i18nKey: null,
    section: HISTORY_SECTION.other,
    type: FIELD_TYPE.text,
    enumSet: undefined,
    path: normalized,
    known: false,
  };
};

/**
 * Returns the reference root a path sits inside, or null.
 *
 * Only paths strictly *below* a root count: `company` itself is a real change
 * ("moved to another company"), while `company.generalData.pib` is a detail of
 * a record shown elsewhere.
 *
 * @param {string} modelName - Model the record belongs to.
 * @param {string} path - Field path.
 * @returns {string|null} The root path, or null when the path is not inside one.
 */
export const getReferenceRoot = (modelName, path) => {
  const normalized = normalizeFieldPath(path);
  const roots = REFERENCE_ROOTS[modelName] || [];

  for (const root of roots) {
    if (normalized.startsWith(`${root}.`)) return root;
  }
  return null;
};

export const isKnownModel = (modelName) =>
  Object.prototype.hasOwnProperty.call(BY_MODEL, modelName);
